import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Shield, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookTestPage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", preferredDate: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/book-test`, form);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <main>
      <div className="min-h-screen">
        <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-blue-50">
          <div className="max-w-7xl mx-auto text-center">
            <Badge className="mb-4 bg-green-100 text-green-700 hover:bg-green-100 text-lg px-6 py-2">
              100% Free - No Obligation
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Get Your Free<span className="text-blue-600"> Radon Test</span>
            </h1>
            <p className="text-2xl text-gray-600 max-w-3xl mx-auto mb-6">
              Know your radon levels. Protect your family. It's free.
            </p>
          </div>
        </section>

        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="animate-slide-in-left">
                <Card className="border-2 border-blue-100 shadow-xl">
                  <CardHeader className="bg-gradient-to-br from-blue-50 to-white">
                    <CardTitle className="text-2xl">Request Your Free Test</CardTitle>
                    <p className="text-gray-600 mt-2">Fill out the form below and we'll contact you within 24 hours to schedule your test.</p>
                  </CardHeader>
                  <CardContent className="p-8">
                    {submitted ? (
                      <div className="text-center py-12" data-testid="book-test-success">
                        <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Shield className="h-8 w-8 text-green-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                        <p className="text-gray-600">We'll contact you within 24 hours to schedule your free radon test.</p>
                      </div>
                    ) : (
                      <form className="space-y-6" onSubmit={handleSubmit} data-testid="book-test-form">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-sm font-semibold text-gray-700">Full Name *</Label>
                          <Input id="name" name="name" required placeholder="John Smith" value={form.name} onChange={handleChange} className="border-2 focus:border-blue-500" data-testid="book-test-name" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-sm font-semibold text-gray-700">Phone Number *</Label>
                          <Input id="phone" name="phone" required placeholder="(403) 555-1234" type="tel" value={form.phone} onChange={handleChange} className="border-2 focus:border-blue-500" data-testid="book-test-phone" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-sm font-semibold text-gray-700">Email Address *</Label>
                          <Input id="email" name="email" required placeholder="john@example.com" type="email" value={form.email} onChange={handleChange} className="border-2 focus:border-blue-500" data-testid="book-test-email" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="address" className="text-sm font-semibold text-gray-700">Property Address *</Label>
                          <Input id="address" name="address" required placeholder="123 Main Street SW, Calgary, AB" value={form.address} onChange={handleChange} className="border-2 focus:border-blue-500" data-testid="book-test-address" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="preferredDate" className="text-sm font-semibold text-gray-700">Preferred Date</Label>
                          <Input id="preferredDate" name="preferredDate" type="date" min={today} value={form.preferredDate} onChange={handleChange} className="border-2 focus:border-blue-500" data-testid="book-test-date" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="message" className="text-sm font-semibold text-gray-700">Additional Information (Optional)</Label>
                          <Textarea id="message" name="message" placeholder="Any specific concerns or questions?" rows={4} value={form.message} onChange={handleChange} className="border-2 focus:border-blue-500 resize-none" data-testid="book-test-message" />
                        </div>
                        <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg" disabled={submitting} data-testid="book-test-submit-btn">
                          {submitting ? "Submitting..." : "Book Free Test"}
                        </Button>
                        <p className="text-sm text-gray-600 text-center">
                          By submitting this form, you agree to be contacted by ZARC Radon Solutions regarding your radon test.
                        </p>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="animate-slide-in-right space-y-6">
                <Card className="border-2 border-green-100 bg-green-50">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Shield className="h-6 w-6 text-green-600" />
                      <span>Why Offer Free Testing?</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">
                      We believe every Calgary family deserves to know their radon levels. Our free testing program removes financial barriers to radon awareness. There's absolutely no obligation to proceed with mitigation services.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-blue-100">
                  <CardHeader>
                    <CardTitle>Prefer to Talk Directly?</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-gray-700 mb-4">Email us at:</p>
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-blue-600" />
                      <a href="mailto:info.zarcradon@gmail.com" className="text-blue-600 hover:text-blue-700 font-semibold" data-testid="email-link">
                        info.zarcradon@gmail.com
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
